/**
 * HTML frame posters: a picture of the top of a page for its gallery card.
 *
 * The page's own iframe renders the poster through the frame bridge
 * (`captureHtmlFramePoster`) and measures its first screen, so nothing runs
 * outside its opaque origin. The app treats the reply as untrusted: it
 * accepts only a JPEG or PNG data URL of at most {@link MAX_POSTER_BYTES},
 * decodes it as an image, reads its average colour, bounds the measure
 * (`coverFromMeasure`), uploads the image and stores both for one theme.
 * Every failure ends quietly: the card keeps its placeholder.
 */
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import {
  posterFailure,
  storeFramePoster,
  type PosterOutcome,
} from "@/lib/frame-poster-store";
import { hexColor } from "@/components/shapes/shared/card-tint";
import { captureHtmlFramePoster } from "./html-frame-bridge";
import { coverFromMeasure } from "./html-cover-measure";

const MAX_POSTER_BYTES = 1.5 * 1024 * 1024;
const POSTER_DATA_URL = /^data:image\/(jpeg|png);base64,/;
const SOURCE_MAP_COMMENT = /\n\/\/# sourceMappingURL=\S*\s*$/;

let captureScript: Promise<string> | null = null;

/** `html-to-image`'s UMD build as text, loaded on the first capture only. */
function loadCaptureScript(): Promise<string> {
  captureScript ??= import("html-to-image/dist/html-to-image.js?raw").then(
    (module) => module.default.replace(SOURCE_MAP_COMMENT, ""),
    (error: unknown) => {
      captureScript = null;
      throw error;
    },
  );
  return captureScript;
}

/**
 * The image a frame's `dataUrl` reply carries, or `null` unless it is a
 * base64 JPEG or PNG of at most {@link MAX_POSTER_BYTES}.
 */
export function readPosterDataUrl(dataUrl: unknown): Blob | null {
  if (typeof dataUrl !== "string") return null;
  const match = POSTER_DATA_URL.exec(dataUrl);
  if (!match) return null;
  const base64 = dataUrl.slice(match[0].length);
  // Checked before decoding: base64 holds 3 bytes per 4 characters.
  if (base64.length > Math.ceil(MAX_POSTER_BYTES / 3) * 4) return null;
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return null;
  }
  if (binary.length === 0 || binary.length > MAX_POSTER_BYTES) return null;
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new Blob([bytes], { type: `image/${match[1]}` });
}

/** The image's average colour as `#rrggbb`, else null (no 2D canvas). */
function averageColor(bitmap: ImageBitmap): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(bitmap, 0, 0, 1, 1);
  const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
  return hexColor(`rgb(${red}, ${green}, ${blue})`);
}

/**
 * `blob` decoded: `null` unless it is an image with a size, else its
 * average colour (null when it cannot be read).
 */
async function decodePoster(
  blob: Blob,
): Promise<{ placeholder: string | null } | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    try {
      if (bitmap.width <= 0 || bitmap.height <= 0) return null;
      let placeholder: string | null = null;
      try {
        placeholder = averageColor(bitmap);
      } catch {
        placeholder = null;
      }
      return { placeholder };
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

/** The card surface the poster sits on, painted behind a transparent page. */
function cardSurfaceColor(): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue("--card-surface")
    .trim();
}

export interface HtmlPosterCapture {
  iframe: HTMLIFrameElement;
  projectId: string;
  frameId: string;
  theme: FrameThumbnailTheme;
  /** SHA-256 of the `content.html` the iframe renders (`sha256Hex`). */
  contentSha256: string;
}

/**
 * Captures, uploads and stores the poster of `frameId` for `theme`. Never
 * rejects; resolves how the capture ended.
 */
export async function captureHtmlPoster({
  iframe,
  projectId,
  frameId,
  theme,
  contentSha256,
}: HtmlPosterCapture): Promise<PosterOutcome> {
  try {
    const script = await loadCaptureScript();
    const reply = await captureHtmlFramePoster(
      iframe,
      script,
      cardSurfaceColor(),
    );
    if (reply === undefined) return "retry";
    const poster = readPosterDataUrl(reply?.dataUrl);
    const decoded = poster ? await decodePoster(poster) : null;
    if (!poster || !decoded) return "rejected";
    const extension = poster.type === "image/png" ? "png" : "jpg";
    await storeFramePoster(
      projectId,
      frameId,
      new File([poster], `poster-${theme}.${extension}`, { type: poster.type }),
      {
        theme,
        contentSha256,
        cover: coverFromMeasure(reply?.measure, decoded.placeholder),
      },
    );
    return "stored";
  } catch (error) {
    return posterFailure(error);
  }
}
